<template>
  <b-row class="justify-content-md-center m-0">
    <b-col lg="6" class="mb-4">
      <b-spinner v-if="isFetching" />

      <b-row v-else>
        <b-col v-if="learningUnit">
          <base-bread-crumb :items="breadcrumbs" />

          <b-button
            variant="outline-primary"
            class="mb-4 btn__previous-page"
            @click="gotoPreviousPage()"
          >
            Back to previous page
          </b-button>

          <div v-if="learningUnit" class="h5 display-4 font-weight-bold mb-3 header__lu-title">
            {{ learningUnit.name }}
          </div>

          <base-card-post-schedule
            v-if="post"
            :post="post"
            :post-schedule="postSchedule"
            :is-private="!post.is_published"
            :show-date="post.is_published"
            :section-id="sectionId"
            :section="section"
            :is-show-video-player="false"
            :is-show-view-recording="false"
            :is-show-attachment="false"
            :is-owner="isOwner"
          />
        </b-col>
      </b-row>
    </b-col>

    <b-col ref="page" lg="8">
      <b-row v-if="!isFetching">
        <b-col md="8">
          <base-video-player
            v-if="post.vimeo"
            :key="`video-${videoWidth}`"
            :video="post.vimeo"
            class="video__recording mb-4"
            :width="videoWidth"
            :height="videoHeight"
          />

          <b-card v-else class="font-weight-bold">
            <b-card-text class="text-center">Recording not available at this time yet</b-card-text>
          </b-card>
        </b-col>
        <b-col md="4">
          <base-chat-message v-if="chatMessageFile" :link="chatMessageFile.url" />

          <b-card v-else class="font-weight-bold">
            <b-card-text class="text-center"> No chat messages available </b-card-text>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import BaseBreadCrumb from '../../components/base-components/BaseBreadCrumb';
import { LearningUnit, Post, Section, User } from '../../models';
import PostSchedule from '../../models/PostSchedule';
import BaseCardPostSchedule from '../../components/base-components/BaseCardPostSchedule';
import BaseVideoPlayer from '../../components/base-components/BaseVideoPlayer';
import BaseChatMessage from '../../components/base-components/BaseChatMessage';
import { pageWidthChecker } from '@/mixins';

export default {
  name: 'PageTeacherVideoConference',

  components: {
    BaseBreadCrumb,
    BaseCardPostSchedule,
    BaseVideoPlayer,
    BaseChatMessage
  },
  mixins: [pageWidthChecker],

  data() {
    return {
      pageWidth: null,
      isFetching: false,
      chatMessages: []
    };
  },

  computed: {
    user() {
      return User.query().first();
    },

    isOwner() {
      return this.learningUnit && this.user ? this.user.uid === this.learningUnit.uid : false;
    },

    learningUnitId() {
      return parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.find(this.learningUnitId);
    },

    postId() {
      return parseInt(this.$route.params.postId);
    },

    post() {
      return Post.query().with('meeting').with('vimeo').with('attachments').find(this.postId);
    },

    sectionId() {
      return parseInt(this.$route.params.sectionId);
    },

    section() {
      return Section.find(this.sectionId);
    },

    postSchedule() {
      return PostSchedule.query()
        .where('post_id', this.postId)
        .where('section_id', this.sectionId)
        .first();
    },

    videoWidth() {
      return this.pageWidth ? this.pageWidth * 0.65 : this.windowWidth <= 650 ? 500 : 646;
    },

    videoHeight() {
      return this.windowWidth <= 650 ? 250 : 343;
    },

    chatMessageFile() {
      return this.post && this.post.attachments ? this.post.attachments[0] : null;
    },

    breadcrumbs() {
      const { learningUnitId, sectionId } = this;

      return [
        {
          key: 'design-and-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name || '',
          to: {
            name: 'design-planning/info',
            params: {
              learningUnitId
            }
          }
        },
        {
          key: 'teaching-feed',
          name: 'Teaching feed',
          to: {
            name: 'design-planning/teaching-feed',
            params: {
              learningUnitId,
              sectionId
            },
            query: {
              ...this.$route.query
            }
          }
        }
      ];
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    }
  },

  methods: {
    onRouteChanged() {
      this.fetch();
    },

    async fetch() {
      this.isFetching = true;

      try {
        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

        await PostSchedule.api().findAll({
          post: this.postId,
          section: this.sectionId
        });

        await Post.api().fetchById(this.postId);
      } catch (error) {
        console.error(error);
        this.$toast.open({
          message: `Fail to load video conference, please try again.`,
          type: 'error',
          duration: 5000
        });
      } finally {
        this.isFetching = false;
      }
    },

    gotoPreviousPage() {
      this.$router.push({
        name: 'design-planning/teaching-feed',
        params: {
          learningUnitId: this.learningUnitId,
          sectionId: this.sectionId
        },
        query: { ...this.$route.query }
      });
    }
  }
};
</script>

<style lang="scss" scoped></style>
